import { holidayWindows, type Holiday, type HoursSchedule, type HoursScheduleInput } from "./configuration.js";

/**
 * The one reading of a schedule (#265): the lander makes it at call time
 * in the customer's account and the portal shows the same answer as
 * "right now", so the two can never disagree. A holiday is a closed
 * window in the schedule's zone - `from <= now < to` on local wall time -
 * and outside every window the weekly windows decide.
 */
export interface HoursReading {
  open: boolean;
  reason: "open" | "closed" | "holiday";
  /** The window that closed it, when `reason` is `holiday`. */
  holiday?: Holiday;
}

/** Local wall-clock parts in a zone, without a date library. An unknown zone reads as UTC rather than throwing on a live call. */
export function localWallTime(timezone: string, now: Date): { day: number; minutes: number; stamp: string } {
  const options: Intl.DateTimeFormatOptions = { weekday: "short", hour: "2-digit", minute: "2-digit", year: "numeric", month: "2-digit", day: "2-digit", hourCycle: "h23" };
  let fmt: Intl.DateTimeFormat;
  try {
    fmt = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: timezone });
  } catch {
    fmt = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" });
  }
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  const hour = Number(parts["hour"]) % 24;
  const minute = Number(parts["minute"]);
  return {
    day: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(String(parts["weekday"])),
    minutes: hour * 60 + minute,
    stamp: `${parts["year"]}-${parts["month"]}-${parts["day"]}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
  };
}

/** The holiday window `stamp` (local `YYYY-MM-DDTHH:MM`) falls in, if any. A row still in the one-day shape is read as its windows. */
export function activeHoliday(holidays: NonNullable<HoursScheduleInput["holidays"]> | undefined, stamp: string): Holiday | undefined {
  for (const h of holidays ?? []) {
    for (const w of holidayWindows(h)) if (w.from <= stamp && stamp < w.to) return w;
  }
  return undefined;
}

function within(minutes: number, opens: string, closes: string): boolean {
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const from = toMin(opens);
  const to = toMin(closes);
  // A window that ends before it starts runs past midnight.
  return to >= from ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

export function evaluateHours(schedule: HoursSchedule | HoursScheduleInput, now: Date): HoursReading {
  const { day, minutes, stamp } = localWallTime(schedule.timezone, now);
  const holiday = activeHoliday(schedule.holidays, stamp);
  if (holiday) return { open: false, reason: "holiday", holiday };
  const open = schedule.weekly.some((w) => w.day === day && within(minutes, w.opens, w.closes));
  return open ? { open: true, reason: "open" } : { open: false, reason: "closed" };
}

/** An override is in force from its start until (not at) its expiry. The lander resolves a field by it at call time, and the portal's "right now" asks the same question (#484). */
export function overrideInForce(startsAt: string, expiresAt: string | undefined, now: Date): boolean {
  return Date.parse(startsAt) <= now.getTime() && (!expiresAt || Date.parse(expiresAt) > now.getTime());
}

/** The lander's answer for one hours field, before its announcement is resolved: `override` when an override in force closed it. */
export interface HoursFieldReading {
  open: boolean;
  reason: "open" | "closed" | "holiday" | "override";
  holiday?: Holiday;
}

/**
 * One decision for an hours field (#484): an override in force, then a
 * holiday, then the week. `overridden` says `schedule` is an override's
 * value (an emergency close, typically), so a closed answer reports
 * `override` - the lander's "Check emergency close" module relies on it,
 * and the portal names it. No schedule at all reads as closed.
 */
export function readHoursField(schedule: HoursSchedule | HoursScheduleInput | undefined, now: Date, overridden = false): HoursFieldReading {
  const closed = overridden ? "override" : "closed";
  if (!schedule) return { open: false, reason: closed };
  const reading = evaluateHours(schedule, now);
  if (reading.open) return { open: true, reason: "open" };
  if (reading.holiday) return { open: false, reason: overridden ? "override" : "holiday", holiday: reading.holiday };
  return { open: false, reason: closed };
}
